import { useCallback, useEffect, useMemo, useState } from 'react';
import { reorder, findItemIndex, moveBetweenLists } from '../utils/dndHelpers';
import { normalizeDragStartEvent, normalizeDragEndEvent } from '../utils/eventNormalizer';

/**
 * Hook para manejar listas ordenables con @dnd-kit
 *
 * Proporciona estado y lógica de reordenamiento
 *
 * @param {Object} options
 * @param {Array} options.items - Lista inicial de items
 * @param {string} [options.idKey='id'] - Key para identificar items (puede ser 'id', 'guid', etc.)
 * @param {Function} [options.onReorder] - Callback cuando se reordena la lista
 * @param {string} [options.droppableId='droppable'] - ID del droppable para normalizar resultados
 * @returns {Object} Estado y funciones para manejar la lista
 */
function useSortableList({ items: initialItems = [], idKey = 'id', onReorder, droppableId = 'droppable' }) {
  const [items, setItems] = useState(initialItems);
  const [activeId, setActiveId] = useState(null);

  // Sincronizar items cuando cambian externamente
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Array de IDs para SortableContext
  const itemIds = useMemo(() => {
    return items.map((item) => {
      const id = item[idKey];
      return typeof id === 'number' ? String(id) : id;
    });
  }, [items, idKey]);

  // Handler para cuando inicia el drag
  const handleDragStart = useCallback((rawEvent) => {
    const event = normalizeDragStartEvent(rawEvent);
    setActiveId(event.activeId);
  }, []);

  // Handler para cuando termina el drag
  const handleDragEnd = useCallback(
    (rawEvent) => {
      const event = normalizeDragEndEvent(rawEvent);

      setActiveId(null);

      if (!event.overId || event.activeId === event.overId) {
        return;
      }

      const oldIndex = findItemIndex(items, event.activeId, idKey);
      const newIndex = findItemIndex(items, event.overId, idKey);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reorderedItems = reorder(items, oldIndex, newIndex);
      setItems(reorderedItems);

      if (onReorder) {
        // Llamar callback con los items reordenados y el resultado normalizado
        onReorder(reorderedItems, {
          source: { droppableId, index: oldIndex },
          destination: { droppableId, index: newIndex },
          draggableId: String(event.activeId),
        });
      }
    },
    [items, idKey, onReorder, droppableId]
  );

  // Función para reordenar manualmente
  const reorderItems = useCallback(
    (startIndex, endIndex) => {
      const reorderedItems = reorder(items, startIndex, endIndex);
      setItems(reorderedItems);
      return reorderedItems;
    },
    [items]
  );

  // Función para mover item a otra lista
  const moveItem = useCallback((sourceList, destinationList, sourceIndex, destinationIndex) => {
    return moveBetweenLists(sourceList, destinationList, sourceIndex, destinationIndex);
  }, []);

  // Item actualmente siendo arrastrado
  const activeItem = useMemo(() => {
    if (!activeId) return null;
    return items.find((item) => String(item[idKey]) === String(activeId));
  }, [activeId, items, idKey]);

  return {
    // Estado
    items,
    setItems,
    activeId,
    activeItem,
    itemIds,

    // Handlers de eventos
    handleDragStart,
    handleDragEnd,

    // Funciones utilitarias
    reorderItems,
    moveItem,
  };
}

export default useSortableList;
